import { hc } from "hono/client";
import type { AppType } from "@dishour/api";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The web app and API share the same origin, so a relative path is enough.
 * `credentials: same-origin` automatically attaches the session cookie.
 */
export const api = hc<AppType>("/", {
  init: { credentials: "same-origin" },
});

/** Korean copy for the better-auth error codes an end user can reach. */
const AUTH_MESSAGES: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: "이메일 또는 비밀번호가 올바르지 않습니다.",
  EMAIL_PASSWORD_SIGN_UP_DISABLED: "가입은 초대를 통해서만 가능합니다.",
  USER_NOT_FOUND: "이메일 또는 비밀번호가 올바르지 않습니다.",
};

export async function unwrap<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T;

  let code = "unknown";
  let message = "일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.";
  let details: unknown;
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string; details?: unknown };
      // better-auth answers with a flat { code, message } instead of our envelope.
      code?: string;
      message?: string;
    };
    if (body.error?.code) code = body.error.code;
    if (body.error?.message) message = body.error.message;
    details = body.error?.details;

    if (!body.error && body.code) {
      code = body.code;
      // better-auth's own copy is English; translate the cases a user can actually hit
      // rather than showing "일시적인 오류" for an ordinary wrong password.
      message = AUTH_MESSAGES[body.code] ?? message;
    }
  } catch {
    // Non-JSON error responses (proxy/gateway errors) use the default message.
  }
  throw new ApiError(res.status, code, message, details);
}
